import { computed } from 'vue'
import { isEmptyObject } from '@/common/converter/utils/is-empty-object'
import { showBillingPopup } from '@/common/utils/common'
import { normalizePurchaseParams } from '@/common/utils/braze.util'
import queryString from '@/common/utils/querystring'
import {
  FIRST_MONTH_PROMOTION_CONFIRM,
  PROMOTION_CANCEL_WARNING_BUTTON,
  VOUCHER_TRANSFER_CONFIRM,
} from '@/common/resources/const'
import { modalManager } from '@/common/utils/modal/modal-manager'
import { voucherModal } from '@/common/utils/modal/voucher-modal'
import { useRouter, useRoute } from 'vue-router/composables'
import { PATH_NAME_VOUCHER_TRANSFER } from '@/common/resources/router.const'

const CALLBACK_ACTION = `purchaseComplete`
const CALLBACK_TRANSFER_ACTION = `transferPurchaseComplete`
export const handleVoucher = () => {
  const { closeLayerPopup, waitForEvent } = modalManager()
  const { modal } = voucherModal()
  const router = useRouter()
  const route = useRoute()
  const locationOrigin = computed(
    () =>
      window.location.origin ||
      `${window.location.protocol}//${window.location.hostname}`,
  )

  const closePopup = () => closeLayerPopup()

  // actions
  const action = {
    // 100 원 프로모션 해지 경고 모달
    promotionCancelNotificationModal: async (voucherInfo = {}) => {
      modal.openPromotionCancel(voucherInfo)

      const { isSuccess } = await waitForSuccessEvent(
        FIRST_MONTH_PROMOTION_CONFIRM,
      )
      if (isSuccess) {
        onShowBillingPopup(voucherInfo)
      }
    },

    // 100 원 프로모션 모달
    promotionModal: async (voucherInfo = {}) => {
      modal.openPromotion(voucherInfo)

      const { isSuccess, payloads } = await waitForSuccessEvent(
        FIRST_MONTH_PROMOTION_CONFIRM,
      )
      if (isSuccess) {
        const { voucherInfo: newVoucherInfo } = payloads ?? {}
        onShowBillingPopup(newVoucherInfo)
      }
    },

    // 이용권 변경 프로모션 해지 경고 모달
    voucherChangeModal: async (voucherInfo = {}, promotionTitle) => {
      modal.openVoucherChange(voucherInfo, promotionTitle)

      const { isSuccess, payloads } = await waitForSuccessEvent(
        PROMOTION_CANCEL_WARNING_BUTTON,
      )
      return { isSuccess, payloads }
    },

    // 이용권 전환 모달
    voucherTransferModal: async (modalConfig) => {
      modal.openVoucherTransfer(modalConfig)

      await waitForEvent(VOUCHER_TRANSFER_CONFIRM, (payload) => {
        if (payload?.isSuccess) {
          router.replace('/').catch(() => {})
          setTimeout(() => {
            closeLayerPopup()
          }, 500)
        }
      })
    },
  }

  /**
   * 모달 confirm success 유무를 프로미스로 리턴,
   * 리턴 전 로직이 필요 할 경우 callback (handler) 에서 처리
   * @param {string} confirmEventName
   * @returns {Promise<*>|Promise}
   */
  const waitForSuccessEvent = (confirmEventName = '') => {
    return waitForEvent(confirmEventName, (payload) => {
      if (!payload?.isSuccess) {
        return { isSuccess: false, payloads: undefined }
      }

      closePopup()
      return { isSuccess: true, payloads: payload?.payloads }
    })
  }

  /**
   * return URL 생성
   * @param voucherInfo
   * @returns {string}
   */
  const getReturnUrl = (voucherInfo) => {
    const concurrency = voucherInfo?.concurrency?.[0] ?? {}
    const action =
      route.name === PATH_NAME_VOUCHER_TRANSFER
        ? CALLBACK_TRANSFER_ACTION
        : CALLBACK_ACTION
    return queryString.concatParams(
      `${locationOrigin.value}/callback.html?action=${action}`,
      normalizePurchaseParams({ ...voucherInfo, ...concurrency }),
    )
  }

  /**
   * 외부 링크 voucher 구매 (제휴)
   * @param voucherInfo
   * @returns {undefined}
   */
  const onExternalPurchase = (voucherInfo = {}) => {
    if (isEmptyObject(voucherInfo)) {
      return undefined
    }

    const concurrency = voucherInfo?.concurrency?.[0] ?? {}
    const linkTarget = concurrency?.external_link_target ?? ''
    const externalUrl = concurrency?.external_link_pc ?? ''

    window.open(externalUrl, linkTarget)
  }

  /**
   * 결제 팝업 띄우기 (pay letter)
   * @param voucherInfo
   */
  const onShowBillingPopup = (voucherInfo = {}) => {
    const productId = voucherInfo?.concurrency?.[0]?.productid ?? ''
    const returnUrl = getReturnUrl(voucherInfo)
    const canceledUrl = `${locationOrigin.value}/callback.html?action=Cancel`

    showBillingPopup({
      productId,
      productType: 'all',
      returnUrl,
      canceledUrl,
    })
  }

  return {
    modal,
    action,
    closePopup,
    onExternalPurchase,
    onShowBillingPopup,
  }
}
